import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    X, Users, TrendingUp, Download, Phone,
    MessageSquare, Clock, Award, DollarSign, Activity, AlertTriangle,
    ShieldCheck, Calculator, GraduationCap, ThumbsUp, ShieldAlert, CheckCircle2, AlertCircle,
    MessageCircle, Bell, Lock, Percent, Target, Calendar, Sparkles, FileText, ChevronRight, CheckSquare, ScanFace, CheckCircle
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { MOCK_CLIENTS_FULL } from '../../data/mockData';
import { fetchClientsList } from '../../services/nommiaApi';
import { createPortal } from 'react-dom';

// =========================================================
// 1. LIVE FLOOR WIDGET (Manager - LIVE SUPABASE VERSION)
// =========================================================
export const LiveFloorWidget = ({ onAction }) => {
    const [liveAgents, setLiveAgents] = useState([]);
    const [loading, setLoading] = useState(true);

    // --- BROADCAST STATES ---
    const [showBroadcast, setShowBroadcast] = useState(false);
    const [message, setMessage] = useState('');
    const [targetType, setTargetType] = useState('all');
    const [targetValue, setTargetValue] = useState('');
    const [isSending, setIsSending] = useState(false);

    useEffect(() => {
        const fetchFloorStatus = async () => {
            try {
                const { data, error } = await supabase
                    .from('profiles')
                    .select('id, full_name, role, status, active_ticket')
                    .neq('role', 'Client')
                    .order('status', { ascending: false });

                if (error) throw error;

                const mappedAgents = data.map((profile, index) => ({
                    id: profile.id || index,
                    name: profile.full_name || 'Unknown Agent',
                    role: profile.role?.replace('_', ' ') || 'Staff',
                    status: profile.status || 'offline',
                    action: profile.active_ticket ? `Ticket #${profile.active_ticket}` : 'Idle',
                    time: profile.status === 'online' ? 'Active' : '-',
                    load: '-',
                    csat: 5.0,
                    activeClient: { name: 'Live Monitored', tier: 'Standard' },
                    activeTicket: { id: profile.active_ticket || 'None', type: 'General' },
                }));

                setLiveAgents(mappedAgents);
            } catch (error) {
                console.error('Error fetching live floor:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchFloorStatus();

        const subscription = supabase
            .channel('public:profiles')
            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'profiles' }, (payload) => {
                setLiveAgents(currentAgents =>
                    currentAgents.map(agent => {
                        if (agent.id === payload.new.id) {
                            return {
                                ...agent,
                                status: payload.new.status || 'offline',
                                action: payload.new.active_ticket ? `Ticket #${payload.new.active_ticket}` : 'Idle',
                                time: payload.new.status === 'online' ? 'Active' : '-',
                            };
                        }
                        return agent;
                    })
                );
            })
            .subscribe();

        return () => supabase.removeChannel(subscription);
    }, []);

    const handleBroadcast = async () => {
        if (!message.trim()) return;
        setIsSending(true);

        try {
            const { data: { user } } = await supabase.auth.getUser();
            const senderEmail = user?.email || 'System Admin';

            const { error } = await supabase.from('crm_broadcasts').insert([{
                sender_email: senderEmail,
                message: message.trim(),
                target_type: targetType,
                target_value: targetType === 'all' ? 'everyone' : targetValue
            }]);

            if (error) throw error;

            setMessage('');
            setTargetValue('');
            setTargetType('all');
            setShowBroadcast(false);
            console.log("Broadcast fired successfully!");

        } catch (err) {
            console.error("Broadcast failed:", err);
        } finally {
            setIsSending(false);
        }
    };

    const onlineCount = liveAgents.filter(a => a.status === 'online').length;
    const busyCount = liveAgents.filter(a => a.status === 'busy').length;

    return (
        <>
            <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 h-full flex flex-col relative">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center gap-4">
                        <h3 className="font-bold text-white flex items-center gap-2 text-lg">
                            <Activity size={20} className="text-blue-400" /> Live Floor Monitor
                        </h3>
                        <div className="flex gap-2">
                            <span className="text-[10px] bg-green-500/10 text-green-400 px-2 py-1 rounded border border-green-500/20 font-bold">
                                {onlineCount} Online
                            </span>
                            <span className="text-[10px] bg-amber-500/10 text-amber-400 px-2 py-1 rounded border border-amber-500/20 font-bold">
                                {busyCount} Busy
                            </span>
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <button className="p-2 hover:bg-slate-700 rounded-lg text-slate-400 transition-colors">
                            <Bell size={16} />
                        </button>
                        <button
                            onClick={() => setShowBroadcast(true)}
                            className="text-xs font-bold px-3 py-1.5 rounded transition-colors bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/20"
                        >
                            Broadcast Message
                        </button>
                    </div>
                </div>

                {/* Data Table */}
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="text-xs text-slate-500 border-b border-slate-700">
                                <th className="pb-3 pl-2 font-bold uppercase">Agent</th>
                                <th className="pb-3 font-bold uppercase">Status</th>
                                <th className="pb-3 font-bold uppercase">Current Activity</th>
                                <th className="pb-3 font-bold uppercase">Duration</th>
                                <th className="pb-3 font-bold uppercase">Load</th>
                                <th className="pb-3 font-bold uppercase">CSAT (Day)</th>
                                <th className="pb-3 pr-2 text-right font-bold uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm">
                            {loading ? (
                                <tr><td colSpan="7" className="text-center py-4 text-slate-500">Loading live floor data...</td></tr>
                            ) : liveAgents.length > 0 ? (
                                liveAgents.map(agent => (
                                    <tr key={agent.id} className="group hover:bg-slate-800/50 transition-colors border-b border-slate-700/50 last:border-0">
                                        <td className="py-3 pl-2">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white bg-indigo-600">
                                                    {agent.name.charAt(0)}
                                                </div>
                                                <div>
                                                    <p className="font-bold text-slate-200">{agent.name}</p>
                                                    <p className="text-[10px] text-slate-500">{agent.role}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-3">
                                            <div className={`flex items-center gap-2 text-xs font-bold px-2 py-1 rounded w-fit capitalize ${agent.status === 'online' ? 'bg-green-500/10 text-green-400' :
                                                    agent.status === 'busy' ? 'bg-amber-500/10 text-amber-400' :
                                                        'bg-slate-700/50 text-slate-500'
                                                }`}>
                                                <div className={`w-1.5 h-1.5 rounded-full ${agent.status === 'online' ? 'bg-green-500 animate-pulse' :
                                                        agent.status === 'busy' ? 'bg-amber-500' :
                                                            'bg-slate-500'
                                                    }`} />
                                                {agent.status}
                                            </div>
                                        </td>
                                        <td className="py-3">
                                            <span className="text-slate-300 font-mono text-xs">{agent.action}</span>
                                        </td>
                                        <td className="py-3">
                                            <span className="font-mono text-xs text-slate-400">
                                                <Clock size={12} className="inline mr-1 mb-0.5" />
                                                {agent.time}
                                            </span>
                                        </td>
                                        <td className="py-3">
                                            <span className="text-xs text-slate-400 bg-slate-900 px-2 py-1 rounded border border-slate-800">
                                                {agent.load}
                                            </span>
                                        </td>
                                        <td className="py-3">
                                            <div className="flex items-center gap-1 text-amber-400">
                                                <span className="font-bold text-white">{agent.csat}</span>
                                                <Award size={12} />
                                            </div>
                                        </td>
                                        <td className="py-3 pr-2 text-right opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={() => onAction && onAction('TOGGLE_MONITOR', agent)}
                                                className="text-xs font-bold text-blue-400 hover:text-white mr-3"
                                            >
                                                Monitor
                                            </button>
                                            <button
                                                onClick={() => onAction && onAction('TOGGLE_WHISPER', agent)}
                                                className="text-xs font-bold text-red-400 hover:text-white"
                                            >
                                                Whisper
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="7" className="text-center py-4 text-slate-500">No staff accounts found in database.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* THE PORTAL FIX: Rips the modal out of the widget and sticks it directly to the <body> */}
            {showBroadcast && createPortal(
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
                    <div className="bg-slate-900 border border-blue-500/30 rounded-2xl p-6 w-full max-w-2xl shadow-2xl relative">

                        {/* Close Button */}
                        <button
                            onClick={() => setShowBroadcast(false)}
                            className="absolute top-4 right-4 text-slate-400 hover:text-white text-xl leading-none transition-colors"
                        >
                            ✕
                        </button>

                        <h3 className="font-bold text-white text-lg flex items-center gap-2 mb-6">
                            <MessageSquare size={20} className="text-blue-400" />
                            Floor Broadcast Command
                        </h3>

                        {/* Dropdowns */}
                        <div className="flex flex-col sm:flex-row gap-4 mb-4">
                            <select
                                className="bg-slate-800 border border-slate-700 text-sm text-white rounded-xl px-4 py-3 flex-1 focus:border-blue-500 outline-none transition-colors"
                                value={targetType}
                                onChange={(e) => {
                                    setTargetType(e.target.value);
                                    setTargetValue('');
                                }}
                            >
                                <option value="all">Broadcast to Entire Floor</option>
                                <option value="role">Target Specific Department</option>
                                <option value="user">Target Individual Agent</option>
                            </select>

                            {targetType === 'role' && (
                                <select
                                    className="bg-slate-800 border border-slate-700 text-sm text-white rounded-xl px-4 py-3 flex-1 focus:border-blue-500 outline-none transition-colors"
                                    value={targetValue}
                                    onChange={(e) => setTargetValue(e.target.value)}
                                >
                                    <option value="">Select Department...</option>
                                    <option value="sales">Sales Team</option>
                                    <option value="support">Support Team</option>
                                    <option value="compliance">Compliance</option>
                                </select>
                            )}

                            {targetType === 'user' && (
                                <select
                                    className="bg-slate-800 border border-slate-700 text-sm text-white rounded-xl px-4 py-3 flex-1 focus:border-blue-500 outline-none transition-colors"
                                    value={targetValue}
                                    onChange={(e) => setTargetValue(e.target.value)}
                                >
                                    <option value="">Select Agent...</option>
                                    {liveAgents.map(staff => (
                                        <option key={staff.id} value={staff.id}>{staff.name} ({staff.role})</option>
                                    ))}
                                </select>
                            )}
                        </div>

                        {/* Text Area */}
                        <textarea
                            className="w-full bg-slate-800 border border-slate-700 rounded-xl p-4 text-sm text-white placeholder-slate-500 focus:border-blue-500 outline-none resize-none h-40 mb-6 custom-scrollbar transition-colors"
                            placeholder="Type your urgent floor message here. This will pop up on their screens instantly..."
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                        />

                        {/* Action Bar */}
                        <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
                            <button
                                onClick={() => setShowBroadcast(false)}
                                className="px-6 py-2.5 rounded-xl text-sm font-bold text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleBroadcast}
                                disabled={isSending || !message.trim() || (targetType !== 'all' && !targetValue)}
                                className="bg-blue-600 hover:bg-blue-500 text-white px-8 py-2.5 rounded-xl text-sm font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg shadow-blue-500/20"
                            >
                                {isSending ? 'Transmitting...' : 'Send Broadcast'}
                            </button>
                        </div>

                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

// =========================================================
// 2. DEPOSIT TICKER (Manager)
// =========================================================
export const DepositTickerWidget = () => {
    const transactions = [
        { id: 1, agent: 'Sarah J.', amount: 5000, time: '2m ago' },
        { id: 2, agent: 'Mike T.', amount: 250, time: '14m ago' },
        { id: 3, agent: 'Emma W.', amount: 10000, time: '1h ago' },
    ];

    return (
        <div className="bg-gradient-to-br from-blue-900/40 to-slate-900/40 p-6 rounded-2xl border border-blue-500/20 relative overflow-hidden">
            <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-white flex items-center gap-2">
                    <DollarSign size={18} className="text-green-400" /> Recent FTDs
                </h3>
            </div>
            <div className="space-y-4 relative z-10">
                {transactions.map(tx => (
                    <div key={tx.id} className="flex items-center justify-between border-b border-white/5 pb-2 last:border-0 last:pb-0">
                        <div>
                            <p className="text-white font-bold">${tx.amount.toLocaleString()}</p>
                            <p className="text-[10px] text-blue-300 uppercase font-bold">{tx.agent}</p>
                        </div>
                        <span className="text-[10px] text-slate-400 font-mono">{tx.time}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// =========================================================
// 3. TEAM LEADERBOARD (Manager)
// =========================================================
export const LeaderboardWidget = () => {
    const leaders = [
        { rank: 1, name: 'Sarah Jenkins', sales: 45000 },
        { rank: 2, name: 'Mike Tyson', sales: 32500 },
        { rank: 3, name: 'Emma Watson', sales: 28000 },
    ];

    return (
        <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 h-full">
            <h3 className="font-bold text-white flex items-center gap-2 mb-6">
                <Award size={18} className="text-amber-400" /> Top Performers
            </h3>
            <div className="space-y-4">
                {leaders.map((leader, index) => (
                    <div key={index} className="flex items-center gap-4">
                        <div className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${index === 0 ? 'bg-amber-500 text-black' :
                                index === 1 ? 'bg-slate-400 text-black' :
                                    'bg-orange-700 text-white'
                            }`}>
                            {leader.rank}
                        </div>
                        <div className="flex-1">
                            <div className="flex justify-between mb-1">
                                <span className="text-sm text-white font-medium">{leader.name}</span>
                                <span className="text-sm font-bold text-green-400">${leader.sales.toLocaleString()}</span>
                            </div>
                            <div className="w-full h-1.5 bg-slate-700 rounded-full overflow-hidden">
                                <div className="h-full bg-blue-500" style={{ width: `${(leader.sales / 50000) * 100}%` }}></div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// =========================================================
// 4. MY PERFORMANCE (Agent) - UPDATED FOR EDUCATION FOCUS
// =========================================================
export const MyPerformanceWidget = () => {
    // Target Config
    const eduTarget = 15;
    const eduCurrent = 12;
    const engageTarget = 60; // e.g. minutes or interactions
    const engageCurrent = 45;

    // Calculate total average progress
    const totalProgress = Math.round(
        ((eduCurrent / eduTarget + engageCurrent / engageTarget) / 2) * 100
    );

    return (
        <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 h-full flex flex-col justify-between relative overflow-hidden">

            {/* Header */}
            <div className="flex justify-between items-start mb-4 relative z-10">
                <div>
                    <h3 className="font-bold text-white flex items-center gap-2">
                        {/* Ensure TrendingUp is imported, or swap for Target/BookOpen */}
                        <TrendingUp size={18} className="text-blue-400" /> My Targets
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">Daily Education & Engagement</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-blue-600/20 text-blue-400 flex items-center justify-center font-bold text-sm border border-blue-500/20">
                    {totalProgress}%
                </div>
            </div>

            {/* Progress Bars */}
            <div className="space-y-5 relative z-10">

                {/* Metric 1: Education Signups */}
                <div>
                    <div className="flex justify-between text-xs font-bold mb-1.5">
                        <span className="text-slate-300 flex items-center gap-2">
                            Edu Signups
                        </span>
                        <span className="text-white">{eduCurrent} <span className="text-slate-500">/ {eduTarget}</span></span>
                    </div>
                    <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-blue-500 rounded-full shadow-[0_0_10px_rgba(59,130,246,0.4)] transition-all duration-1000"
                            style={{ width: `${(eduCurrent / eduTarget) * 100}%` }}
                        ></div>
                    </div>
                </div>

                {/* Metric 2: Client Engagement */}
                <div>
                    <div className="flex justify-between text-xs font-bold mb-1.5">
                        <span className="text-slate-300 flex items-center gap-2">
                            Client Engagement
                        </span>
                        <span className="text-white">{engageCurrent} <span className="text-slate-500">/ {engageTarget}</span></span>
                    </div>
                    <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-purple-500 rounded-full shadow-[0_0_10px_rgba(168,85,247,0.4)] transition-all duration-1000"
                            style={{ width: `${(engageCurrent / engageTarget) * 100}%` }}
                        ></div>
                    </div>
                </div>
            </div>

            {/* Background Decoration */}
            <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-blue-500/5 rounded-full blur-2xl pointer-events-none"></div>
        </div>
    );
};

// =========================================================
// 5. CONTACT QUEUE (Filtered & Sorted)
// =========================================================
export const ContactQueueWidget = ({ role }) => {
    const navigate = useNavigate();
    const [queue, setQueue] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState('All'); // <-- NEW: Filter State

    useEffect(() => {
        const fetchLiveQueue = async () => {
            try {
                const listResponse = await fetchClientsList(1, 100);
                let clientArray = [];
                if (Array.isArray(listResponse)) {
                    clientArray = listResponse;
                } else if (typeof listResponse === 'object') {
                    const arrayKey = Object.keys(listResponse).find(k => Array.isArray(listResponse[k]));
                    clientArray = arrayKey ? listResponse[arrayKey] : (listResponse.data || []);
                }

                // FETCH COMPLETED TASKS FROM SUPABASE
                const { data: completedTasks, error: taskError } = await supabase
                    .from('crm_tasks')
                    .select('client_id, task_type')
                    .eq('status', 'completed');
                
                if (taskError) {
                    console.error("Failed to load completed tasks from Supabase:", taskError);
                }

                // Create a fast lookup map for completed tasks
                const completedTaskMap = {};
                (completedTasks || []).forEach(t => {
                    if (!completedTaskMap[t.client_id]) completedTaskMap[t.client_id] = new Set();
                    completedTaskMap[t.client_id].add(t.task_type);
                });

                const liveQueue = [];
                
                clientArray.forEach(client => {
                    const safeId = String(client.accountId || client.id || client._id || client.email);
                    
                    let type = null;
                    let mission = '';
                    let iconColor = '';
                    let priority = '';
                    let icon = null;

                    if (client.kyc?.status === 'VERIFIED') {
                        type = 'Onboarding';
                        mission = 'Welcome Call & Platform Tour';
                        iconColor = 'bg-blue-500/20 text-blue-400';
                        priority = 'Low'; 
                        icon = <CheckCircle size={18} />;
                    }
                    else if (client.kyc?.status === 'PENDING' || client.kyc?.attemptStatus === 'STARTED') {
                        type = 'Compliance';
                        mission = 'Offer Help with KYC Upload';
                        iconColor = 'bg-amber-500/20 text-amber-400';
                        priority = 'Normal';
                        icon = <FileText size={18} />;
                    }
                    else if (client.kyc?.status === 'NOT_VERIFIED' || !client.kyc || !client.kyc.status) {
                        type = 'Activation';
                        mission = 'Push to Start KYC';
                        iconColor = 'bg-slate-500/20 text-slate-400';
                        priority = 'High'; 
                        icon = <Phone size={18} />;
                    }
                    else if (client.membership?.planName === 'Trader' || client.membership?.planName === 'Connect') {
                        type = 'Sales';
                        mission = 'Pitch Pro/Elite Upgrade';
                        iconColor = 'bg-indigo-500/20 text-indigo-400';
                        priority = 'Medium'; 
                        icon = <Award size={18} />;
                    }

                    // ONLY ADD IF IT HAS NOT BEEN COMPLETED IN SUPABASE
                    if (type) {
                        const isCompleted = completedTaskMap[safeId]?.has(type);
                        if (!isCompleted) {
                            const safeName = (client.firstName ? `${client.firstName} ${client.lastName || ''}`.trim() : client.email) || 'Unknown Client';
                            const rawDate = new Date(client.kyc?.updatedAt || client.registrationDate || new Date());
                            liveQueue.push({ id: safeId, name: safeName, type, mission, iconColor, priority, icon, rawDate });
                        }
                    }
                });

                // THE FIX: Sort Oldest to Newest
                liveQueue.sort((a, b) => a.rawDate - b.rawDate);

                setQueue(liveQueue);
            } catch (err) {
                console.error("Failed to load live contact queue:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchLiveQueue();
    }, []);

    // HANDLE TASK COMPLETION
    const handleCompleteTask = async (task, e) => {
        e.stopPropagation();
        
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error("Must be logged in to complete tasks.");

            const { error } = await supabase
                .from('crm_tasks')
                .upsert({
                    client_id: task.id,
                    task_type: task.type,
                    status: 'completed',
                    completed_by: user.id,
                    completed_at: new Date().toISOString()
                }, { onConflict: 'client_id, task_type' });

            if (error) throw error;

            // Optimistically update the UI to instantly remove the task
            setQueue(currentQueue => currentQueue.filter(t => !(t.id === task.id && t.type === task.type)));
            
        } catch (error) {
            console.error("Failed to complete task:", error);
            alert("Failed to complete task. Check console.");
        }
    };

    // Apply the user's dropdown filter
    const displayQueue = filterType === 'All' ? queue : queue.filter(item => item.type === filterType);

    return (
        <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 h-full flex flex-col">
            <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-white flex items-center gap-2">
                    <Phone size={18} className="text-green-400" /> Live Contact Queue
                </h3>
                
                {/* THE FIX: Filter Dropdown */}
                <div className="flex items-center gap-3">
                    <select 
                        value={filterType} 
                        onChange={(e) => setFilterType(e.target.value)}
                        className="bg-slate-900 border border-slate-700 text-slate-300 text-xs rounded px-2 py-1 focus:outline-none focus:border-green-500 cursor-pointer"
                    >
                        <option value="All">All Tasks</option>
                        <option value="Activation">Activation</option>
                        <option value="Compliance">Compliance</option>
                        <option value="Onboarding">Onboarding</option>
                        <option value="Sales">Sales</option>
                    </select>
                    
                    {loading ? (
                        <span className="text-[10px] text-slate-400 animate-pulse">Syncing...</span>
                    ) : (
                        <span className="bg-green-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{displayQueue.length}</span>
                    )}
                </div>
            </div>

            <div className="space-y-3 flex-1 overflow-y-auto pr-2 custom-scrollbar">
                {loading ? (
                    <div className="h-full flex flex-col items-center justify-center text-slate-500">
                        <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin mb-2"></div>
                        <p className="text-xs">Analyzing live client data...</p>
                    </div>
                ) : displayQueue.length > 0 ? displayQueue.map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-800 border border-slate-700 rounded-xl hover:border-green-500/50 transition-all cursor-pointer group">
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold border border-white/5 ${item.iconColor}`}>
                                {item.icon}
                            </div>
                            <div>
                                <h4 className="text-sm font-bold text-white flex items-center gap-2">
                                    {item.name}
                                    {item.priority !== 'Normal' && (
                                        <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase font-bold ${item.priority === 'High' ? 'bg-red-500 text-white' : item.priority === 'Medium' ? 'bg-orange-500 text-white' : 'bg-yellow-500 text-slate-900'}`}>
                                            {item.priority}
                                        </span>
                                    )}
                                </h4>
                                <div className="flex flex-col">
                                    <span className="text-[10px] font-bold text-slate-300 uppercase tracking-wide">{item.type}</span>
                                    <span className="text-[10px] text-slate-500">{item.mission}</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button 
                                onClick={(e) => handleCompleteTask(item, e)} 
                                className="bg-slate-700 hover:bg-green-600 text-slate-300 hover:text-white p-2 rounded-lg transition-colors group/btn" 
                                title="Mark as Completed"
                            >
                                <CheckCircle2 size={16} className="group-hover/btn:scale-110 transition-transform" />
                            </button>
                            <button onClick={() => navigate(`/client/${item.id}`, { state: { activeTab: 'comms' } })} className="bg-slate-700 hover:bg-blue-600 text-slate-300 hover:text-white p-2 rounded-lg transition-colors" title="Open Communication Hub">
                                <MessageCircle size={16} />
                            </button>
                        </div>
                    </div>
                )) : (
                    <div className="h-full flex flex-col items-center justify-center text-slate-500 opacity-60">
                        <CheckCircle size={32} className="mb-2 text-green-500/50" />
                        <p className="text-xs">Queue is clear.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// =========================================================
// 6. TICKET QUEUE (Live Supabase & Real-Time Sync)
// =========================================================
export const TicketQueueWidget = ({ role, onAction, tickets: propTickets = [], unassigned: propUnassigned = [], completed: propCompleted = [] }) => {
    const navigate = useNavigate();
    const [view, setView] = useState('mine'); // 'mine', 'unassigned', or 'completed'
    const [loading, setLoading] = useState(true);
    const [newChatAlert, setNewChatAlert] = useState(null);

    // 🚨 THE FIX: Self-contained state so it doesn't rely on parent filtering!
    const [liveTickets, setLiveTickets] = useState(propTickets);
    const [liveUnassigned, setLiveUnassigned] = useState(propUnassigned);
    const [liveCompleted, setLiveCompleted] = useState(propCompleted);

    // Determine if the viewer is a Manager based on their role
    const isManager = role === 'Admin' || role?.includes('Manager');

    useEffect(() => {
        const fetchLiveTickets = async () => {
            try {
                // 1. Find out exactly who is logged in
                const { data: { user } } = await supabase.auth.getUser();
                if (!user) return;

                // 2. Fetch ALL tickets from the database
                const { data: allTickets, error } = await supabase
                    .from('crm_tickets')
                    .select('*')
                    .order('created_at', { ascending: false });

                if (error) throw error;

                // 3. Intelligently route them into the correct buckets
                const myActive = [];
                const pool = [];
                const done = [];

                allTickets.forEach(t => {
                    const status = (t.status || '').toLowerCase();
                    
                    if (status === 'closed' || status === 'resolved') {
                        // Managers see ALL completed. Agents see only their OWN completed.
                        if (isManager || t.assigned_to === user.id) done.push(t);
                    } else if (!t.assigned_to || t.assigned_to === 'unassigned') {
                        // EVERYONE sees the Unassigned pool to claim tickets
                        pool.push(t);
                    } else {
                        // Managers see ALL active. Agents see only their OWN active.
                        if (isManager || t.assigned_to === user.id) myActive.push(t);
                    }
                });

                setLiveTickets(myActive);
                setLiveUnassigned(pool);
                setLiveCompleted(done);
            } catch (err) {
                console.error("Failed to load live ticket queue:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchLiveTickets();

        // 🚨 THE FIX: Upgraded Real-Time Listener to auto-refresh lists!
        const uniqueChannelName = `live-tickets-${Math.random().toString(36).substring(7)}`;
        const ticketSub = supabase
            .channel(uniqueChannelName)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_tickets' }, (payload) => {
                
                fetchLiveTickets(); // Re-fetch data whenever ANY ticket is updated/claimed

                // Toast Notification for NEW live chats
                if (payload.eventType === 'INSERT' && payload.new.live_chat_id) {
                    try {
                        const alertSound = new Audio('/ping.mp3');
                        alertSound.play().catch(err => console.log("Browser blocked autoplay", err));
                    } catch (e) {}

                    setNewChatAlert(payload.new);
                    setTimeout(() => setNewChatAlert(null), 10000);
                }
            })
            .subscribe();

        return () => supabase.removeChannel(ticketSub);
    }, [role, isManager]);

    // Determine which list to render
    let currentList = liveTickets;
    if (view === 'unassigned') currentList = liveUnassigned;
    if (view === 'completed') currentList = liveCompleted;

    const hasUnassignedLiveChat = liveUnassigned.some(t => t.live_chat_id || (t.subject && t.subject.includes('Live Chat')));

    return (
        <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 h-full flex flex-col relative overflow-hidden">

            {/* Temporary Visual Toast for New Chats */}
            {newChatAlert && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 bg-purple-600 text-white px-4 py-3 rounded-xl shadow-[0_0_20px_rgba(147,51,234,0.4)] flex items-center gap-3 border border-purple-400">
                    <div className="w-2 h-2 bg-white rounded-full animate-ping"></div>
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-purple-200">Incoming Live Chat</p>
                        <p className="text-sm font-bold truncate max-w-[200px]">{newChatAlert.subject || 'Client is waiting...'}</p>
                    </div>
                    <button onClick={() => setNewChatAlert(null)} className="ml-2 text-purple-300 hover:text-white"><X size={16} /></button>
                </div>
            )}

            {/* HEADER TABS & TITLE */}
            <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-white flex items-center gap-2">
                    <MessageSquare size={18} className="text-purple-400" /> Customer Support Tickets
                </h3>
                
                <div className="flex gap-3">
                    <button
                        onClick={() => setView('mine')}
                        className={`font-bold text-sm flex items-center gap-1.5 pb-1 border-b-2 transition-all ${view === 'mine' ? 'text-purple-400 border-purple-400' : 'text-slate-500 border-transparent hover:text-slate-300'}`}
                    >
                        <MessageSquare size={14} /> {isManager ? 'Team Active' : 'My Tickets'}
                        {liveTickets.length > 0 && <span className="bg-purple-500 text-white text-[9px] px-1.5 rounded-full ml-1">{liveTickets.length}</span>}
                    </button>

                    <button
                        onClick={() => setView('unassigned')}
                        className={`font-bold text-sm flex items-center gap-1.5 pb-1 border-b-2 transition-all ${view === 'unassigned' ? 'text-blue-400 border-blue-400' : 'text-slate-500 border-transparent hover:text-slate-300'}`}
                    >
                        <Users size={14} /> Unassigned
                        {liveUnassigned.length > 0 && (
                            <span className={`text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold shadow-lg ml-1 transition-all duration-300 ${hasUnassignedLiveChat ? 'bg-red-500 animate-pulse border border-red-400 shadow-red-500/50' : 'bg-blue-500'}`}>
                                {liveUnassigned.length}
                            </span>
                        )}
                    </button>

                    <button
                        onClick={() => setView('completed')}
                        className={`font-bold text-sm flex items-center gap-1.5 pb-1 border-b-2 transition-all ${view === 'completed' ? 'text-green-400 border-green-400' : 'text-slate-500 border-transparent hover:text-slate-300'}`}
                    >
                        <CheckCircle size={14} /> {isManager ? 'Team Done' : 'Completed'}
                        {liveCompleted.length > 0 && <span className="bg-green-500/20 text-green-400 border border-green-500/30 text-[9px] px-1.5 rounded-full ml-1">{liveCompleted.length}</span>}
                    </button>
                </div>
            </div>

            {/* TICKET LIST */}
            <div className="space-y-3 flex-1 overflow-y-auto pr-2 custom-scrollbar">
                {loading ? (
                    <div className="h-full flex flex-col items-center justify-center text-slate-500">
                        <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mb-2"></div>
                        <p className="text-xs">Fetching queues...</p>
                    </div>
                ) : currentList.length > 0 ? currentList.map(ticket => {
                    let borderClass = 'border-purple-500 hover:border-purple-400';
                    let actionText = 'Open Workspace →';
                    let actionColor = 'text-purple-400';

                    if (view === 'unassigned') {
                        borderClass = 'border-blue-500 hover:border-blue-400';
                        actionText = 'Claim Ticket →';
                        actionColor = 'text-blue-400';
                    } else if (view === 'completed') {
                        borderClass = 'border-green-500/50 hover:border-green-400 opacity-70 hover:opacity-100';
                        actionText = 'View Log →';
                        actionColor = 'text-green-400';
                    }

                    return (
                        <div
                            key={ticket.id}
                            onClick={async () => {
                                const targetId = ticket.client_id || ticket.client_email || ticket.email || 'anonymous';
                                let routingId = targetId;
                                if (targetId === 'anonymous' && !ticket.client?.email && !ticket.live_chat_id && !ticket.subject?.includes('Live Chat')) {
                                    routingId = 'ghost-ticket-bypass'; 
                                }

                                if (view === 'unassigned') {
                                    const { data: { user } } = await supabase.auth.getUser();
                                    if (user) supabase.from('crm_tickets').update({ assigned_to: user.id }).eq('id', ticket.id).then();
                                }

                                navigate(`/client/${routingId}`, {
                                    state: { activeTab: 'tickets', ticketId: ticket.id }
                                });
                            }}
                            className={`p-3 bg-slate-800 rounded-lg border-l-2 transition-all cursor-pointer group hover:bg-slate-700/50 ${borderClass}`}
                        >
                            <div className="flex justify-between items-start mb-1">
                                <div>
                                    <span className={`text-xs font-bold block flex items-center gap-2 ${view === 'completed' ? 'text-slate-400 line-through' : 'text-white'}`}>
                                        {ticket.subject || 'No Subject'}
                                        {ticket.sentiment === 'Angry' && view !== 'completed' && <span className="text-[9px] bg-red-500/20 text-red-400 px-1 rounded uppercase">Angry</span>}
                                    </span>
                                    <span className="text-[10px] text-slate-400">
                                        {ticket.client?.name || ticket.client_name || ticket.client_email || 'Unknown Client'}
                                    </span>
                                </div>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${
                                    view === 'completed' ? 'bg-green-500/10 text-green-500 border border-green-500/20' :
                                    ticket.priority === 'High' ? 'bg-red-500/10 text-red-500' : 
                                    'bg-slate-700 text-slate-400'
                                }`}>
                                    {view === 'completed' ? 'Resolved' : (ticket.priority || 'Normal')}
                                </span>
                            </div>
                            
                            <div className="flex justify-between items-center mt-2">
                                <div className="flex items-center gap-3">
                                    <p className="text-[10px] text-slate-500 flex items-center gap-1">
                                        <Clock size={10} />
                                        {new Date(ticket.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                    
                                    {isManager && view !== 'unassigned' && (
                                        <span className="text-[9px] text-slate-400 bg-slate-900 px-1.5 py-0.5 rounded border border-slate-700">
                                            {view === 'completed' ? 'Closed by: ' : 'Owner: '} 
                                            {ticket.assigned_to || 'Unknown'}
                                        </span>
                                    )}
                                </div>
                                <span className={`text-[10px] font-bold opacity-0 group-hover:opacity-100 transition-opacity ${actionColor}`}>
                                    {actionText}
                                </span>
                            </div>
                        </div>
                    );
                }) : (
                    <div className="h-full flex flex-col items-center justify-center text-slate-500 opacity-60">
                        <CheckCircle2 size={32} className="mb-2 text-slate-600" />
                        <p className="text-xs">
                            {view === 'mine' ? "No active tickets. You're all caught up!" : 
                             view === 'completed' ? "No completed tickets found." :
                             "Queue is clear."}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

// =========================================================
// 7. STAFF ROTA & ATTENDANCE (Manager)
// =========================================================
export const StaffRotaWidget = () => {
    const shifts = [
        { id: 1, name: 'Sarah Jenkins', shift: '08:00 - 16:00', status: 'On Floor', type: 'success' },
        { id: 2, name: 'Mike Tyson', shift: '09:00 - 17:00', status: 'Late (15m)', type: 'warning' },
        { id: 3, name: 'Emma Watson', shift: '09:00 - 17:00', status: 'On Floor', type: 'success' },
        { id: 4, name: 'David Beckham', shift: '12:00 - 20:00', status: 'Scheduled', type: 'neutral' },
        { id: 5, name: 'Jessica Rabbit', shift: '-', status: 'Sick Leave', type: 'error' },
    ];

    return (
        <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 h-full flex flex-col">
            {/* Header - Fixed at Top */}
            <h3 className="font-bold text-white flex items-center gap-2 mb-4 flex-shrink-0">
                <Clock size={18} className="text-blue-400" /> Staff Rota
            </h3>

            {/* List - Scrollable Area */}
            <div className="space-y-3 flex-1 overflow-y-auto min-h-0 custom-scrollbar pr-2">
                {shifts.map(staff => (
                    <div key={staff.id} className="flex justify-between items-center p-2 rounded hover:bg-slate-800/50">
                        <div>
                            <p className="text-sm font-bold text-slate-200">{staff.name}</p>
                            <p className="text-[10px] text-slate-500 font-mono">{staff.shift}</p>
                        </div>
                        <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${staff.type === 'success' ? 'bg-green-500/10 text-green-400' :
                                staff.type === 'warning' ? 'bg-amber-500/10 text-amber-500' :
                                    staff.type === 'error' ? 'bg-red-500/10 text-red-500' :
                                        'bg-slate-700 text-slate-400'
                            }`}>
                            {staff.status}
                        </span>
                    </div>
                ))}
            </div>

            {/* Button - Fixed at Bottom */}
            <div className="mt-4 pt-0 flex-shrink-0">
                <button className="w-full py-2 text-xs text-slate-500 hover:text-white border border-slate-700 rounded transition-colors">
                    View Full Schedule
                </button>
            </div>
        </div>
    );
};

// =========================================================
// 8. KYC CHECKS QUEUE (LIVE NOMMIA API + SUPABASE VAULT)
// =========================================================
export const KYCTaskWidget = () => {
    const navigate = useNavigate();
    const [queue, setQueue] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchLiveKycQueue = async () => {
            try {
                // 1. Fetch pending documents directly from the new Supabase Vault
                const { data: pendingDocs } = await supabase
                    .from('crm_documents')
                    .select('*')
                    .eq('status', 'Pending Review')
                    .order('created_at', { ascending: false });

                // 2. Fetch the legacy Nommia API clients
                const listResponse = await fetchClientsList(1, 100);
                let clientArray = [];
                if (Array.isArray(listResponse)) {
                    clientArray = listResponse;
                } else if (typeof listResponse === 'object') {
                    const arrayKey = Object.keys(listResponse).find(k => Array.isArray(listResponse[k]));
                    clientArray = arrayKey ? listResponse[arrayKey] : (listResponse.data || []);
                }

                // 3. Deduplicate and Merge!
                const pendingQueueMap = new Map();

                // A. Add Supabase Manual Uploads First
                if (pendingDocs) {
                    pendingDocs.forEach(doc => {
                        // 🚨 THE FIX: Force the router to use Email as the primary lookup key
                        const routeKey = doc.client_email || doc.client_id;
                        if (!routeKey) return;
                        
                        if (!pendingQueueMap.has(routeKey)) {
                            pendingQueueMap.set(routeKey, {
                                id: routeKey, // 👈 This will now be an email 99% of the time
                                name: doc.client_email || 'Unknown Client', 
                                email: doc.client_email,
                                date: new Date(doc.created_at).toLocaleDateString(),
                                statusText: 'PENDING REVIEW'
                            });
                        }
                    });
                }

                // B. Add Legacy Pending & Map Real Names to Supabase Records
                clientArray.forEach(client => {
                    const isLegacyPending = (
                        client.kyc?.status === 'PENDING' || 
                        client.kyc?.status === 'SUBMITTED' || 
                        client.kyc?.status === 'PROCESSING' || 
                        client.kycStatus === 'Pending' || 
                        client.kycStatus === 'Pending Review'
                    );

                    // 🚨 THE FIX: Prioritize Email for routing! Only fallback to IDs if email is missing.
                    const safeId = client.email || client.accountId || client.id || client._id;
                    const safeName = (client.firstName ? `${client.firstName} ${client.lastName || ''}`.trim() : client.email) || 'Unknown Client';
                    const emailKey = (client.email || '').toLowerCase();

                    if (isLegacyPending || pendingQueueMap.has(emailKey) || pendingQueueMap.has(safeId)) {
                        const dateRaw = client.kyc?.updatedAt || client.registrationDate;
                        
                        // Always merge using the email if possible to bridge the two databases
                        const targetKey = pendingQueueMap.has(emailKey) ? emailKey : safeId;
                        
                        pendingQueueMap.set(targetKey, {
                            id: targetKey, // 👈 This forces the URL to be /client/email@domain.com
                            name: safeName, 
                            email: client.email,
                            date: pendingQueueMap.has(targetKey) ? pendingQueueMap.get(targetKey).date : (dateRaw ? new Date(dateRaw).toLocaleDateString() : 'Recent'),
                            statusText: 'PENDING REVIEW'
                        });
                    }
                });

                // C. Convert map back to an array for the UI
                const finalQueue = Array.from(pendingQueueMap.values());
                setQueue(finalQueue);

            } catch (err) {
                console.error("Failed to load live KYC queue:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchLiveKycQueue();
    }, []);

    return (
        <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 h-full flex flex-col">
            <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-white flex items-center gap-2">
                    <FileText size={18} className="text-amber-400" /> Pending KYC Checks
                </h3>
                {loading ? (
                    <span className="text-[10px] text-slate-400 animate-pulse">Scanning DB...</span>
                ) : (
                    <span className="bg-amber-500 text-slate-900 text-[10px] font-bold px-1.5 py-0.5 rounded-full">{queue.length}</span>
                )}
            </div>

            <div className="space-y-3 flex-1 overflow-y-auto pr-2 custom-scrollbar">
                {loading ? (
                    <div className="h-full flex flex-col items-center justify-center text-slate-500">
                        <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mb-2"></div>
                        <p className="text-xs">Finding pending documents...</p>
                    </div>
                ) : queue.length > 0 ? queue.map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-800 border border-slate-700 rounded-xl hover:border-amber-500/50 transition-all group">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-amber-500/10 text-amber-400 flex items-center justify-center border border-amber-500/20">
                                <AlertCircle size={18} />
                            </div>

                            <div>
                                <h4 className="text-sm font-bold text-white">{item.name}</h4>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-[10px] font-bold text-amber-400 uppercase tracking-wide">{item.statusText}</span>
                                    <span className="text-[10px] text-slate-500">• Submitted: {item.date}</span>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={() => navigate(`/client/${item.id}`, { state: { activeTab: 'kyc' } })}
                            className="bg-amber-500/10 hover:bg-amber-500 text-amber-400 hover:text-slate-900 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors"
                        >
                            Review
                        </button>
                    </div>
                )) : (
                    <div className="h-full flex flex-col items-center justify-center text-slate-500 opacity-60">
                        <CheckCircle size={32} className="mb-2 text-green-500/50" />
                        <p className="text-xs">No pending documents to review.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// =========================================================
// 9. MARKET EVENTS (New Idea for Agents)
// =========================================================
export const MarketEventsWidget = () => {
    return (
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-6 rounded-2xl border border-slate-700 h-full">
            <h3 className="font-bold text-white flex items-center gap-2 mb-4">
                <TrendingUp size={18} className="text-green-400" /> Market Movers
            </h3>
            <div className="space-y-4">
                <div className="flex items-start gap-3">
                    <div className="text-center bg-slate-800 p-2 rounded">
                        <span className="block text-xs text-slate-400">USD</span>
                        <span className="block text-sm font-bold text-white">NFP</span>
                    </div>
                    <div>
                        <p className="text-xs text-red-400 font-bold uppercase">High Impact • 14:30</p>
                        <p className="text-sm text-slate-300">Non-Farm Payrolls expected to trigger gold volatility.</p>
                    </div>
                </div>
                <div className="w-full h-px bg-slate-700"></div>
                <div className="flex items-start gap-3">
                    <div className="text-center bg-slate-800 p-2 rounded">
                        <span className="block text-xs text-slate-400">BTC</span>
                        <span className="block text-sm font-bold text-white">ETF</span>
                    </div>
                    <div>
                        <p className="text-xs text-amber-400 font-bold uppercase">Med Impact • All Day</p>
                        <p className="text-sm text-slate-300">Bitcoin holding support level at 65k.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

// =========================================================
// 10. UNIFIED SUPER WIDGET (Aligned with NOM-HR-BONUS-001)
// =========================================================
export const HybridUnifiedWidget = ({ role = 'Hybrid_Agent' }) => {

    // --- 1. RATES & CONFIG FROM DOCUMENT ---
    const RATES = {
        salary: { sales: 1800, support: 2200, hybrid: 2000 },
        comms: { pro: 4.00, elite: 6.00 },
        salesPot: { funded: 10.00, trader: 5.00 },
        servicePot: { ticket: 0.50, review: 2.00, kyc: 1.00 }
    };

    // --- 2. MOCK ACTIVITY DATA ---
    const activity = {
        // Sales Activities
        proSales: role === 'Support_L1' ? 0 : 5,
        eliteSales: role === 'Support_L1' ? 0 : 2,
        funded: role === 'Support_L1' ? 0 : 8,
        activeTraders: role === 'Support_L1' ? 0 : 3,

        // Service Activities
        tickets: role === 'Sales_L1' ? 0 : 450,
        reviews: role === 'Sales_L1' ? 0 : 15,
        manualKyc: role === 'Sales_L1' ? 0 : 20,

        // Quality Metrics
        retention: 82, // 1.2x Multiplier
        education: 45, // 1.15x Multiplier
        csat: 85       // 100% Payout
    };

    // --- 3. CALCULATE PAY ---

    // A. Base Salary
    const baseSalary = role === 'Sales_L1' ? RATES.salary.sales
        : role === 'Support_L1' ? RATES.salary.support
            : RATES.salary.hybrid;

    // B. Component A: Membership Commissions (Direct Pay - Not Multiplied)
    const commsTotal = (activity.proSales * RATES.comms.pro) + (activity.eliteSales * RATES.comms.elite);

    // C. Component B: The Base Pool (Activity Basket)
    const salesPot = (activity.funded * RATES.salesPot.funded) + (activity.activeTraders * RATES.salesPot.trader);
    const servicePot = (activity.tickets * RATES.servicePot.ticket) + (activity.reviews * RATES.servicePot.review) + (activity.manualKyc * RATES.servicePot.kyc);

    // Total Pool subject to multipliers
    const totalBasePool = salesPot + servicePot;

    // D. Multipliers (Apply to Base Pool Only)
    // Retention Tier (Target 90% for 1.3x)
    const retMult = activity.retention >= 90 ? 1.30
        : activity.retention >= 80 ? 1.20
            : activity.retention >= 70 ? 1.10
                : 1.00;

    // Education Tier (Target 70% for 1.3x)
    const eduMult = activity.education >= 70 ? 1.30
        : activity.education >= 50 ? 1.20
            : activity.education >= 40 ? 1.15
                : activity.education >= 30 ? 1.10
                    : 1.00;

    const boostedPool = totalBasePool * retMult * eduMult;

    // E. CSAT Gate (Payout Percentage)
    const csatPayoutPct = activity.csat >= 80 ? 1.00
        : activity.csat >= 70 ? 0.90
            : activity.csat >= 60 ? 0.50
                : 0.00;

    const finalPoolBonus = boostedPool * csatPayoutPct;

    // F. TOTAL PROJECTED PAY
    const projectedTotal = baseSalary + commsTotal + finalPoolBonus;

    // --- 4. RENDER HELPERS ---
    const isCompliant = true; // "Zero Tolerance" Gate

    return (
        <div className="w-full bg-gradient-to-r from-indigo-900 via-slate-900 to-slate-900 p-0 rounded-2xl border border-indigo-500/30 h-full relative overflow-hidden flex flex-col xl:flex-row shadow-2xl">
            <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none"><Target size={200} /></div>

            {/* --- SECTION 1: THE PAYCHECK --- */}
            <div className="w-full xl:w-[25%] bg-slate-900/50 p-6 flex flex-col justify-center border-b xl:border-b-0 xl:border-r border-white/5">
                <div className="flex items-center gap-2 mb-3">
                    <div className="p-1.5 bg-green-500/20 rounded-md text-green-400"><DollarSign size={16} /></div>
                    <span className="text-xs font-bold text-indigo-300 uppercase tracking-wider">Projected Pay</span>
                </div>
                <div className="text-3xl lg:text-4xl font-bold text-white tracking-tighter mb-1">
                    €{projectedTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <p className="text-[10px] text-slate-400 mb-4">M+1 Payroll Estimate</p>

                <div className="space-y-2 text-xs">
                    <div className="flex justify-between border-b border-white/5 pb-1">
                        <span className="text-slate-500">Base Salary</span>
                        <span className="text-slate-300 font-mono">€{baseSalary.toLocaleString()}</span>
                    </div>
                    {/* Show Comms only if role has sales */}
                    {role !== 'Support_L1' && (
                        <div className="flex justify-between border-b border-white/5 pb-1">
                            <span className="text-slate-500">Direct Comms</span>
                            <span className="text-green-400 font-mono">+€{commsTotal.toFixed(2)}</span>
                        </div>
                    )}
                    <div className="flex justify-between items-center">
                        <span className="text-slate-500">Activity Bonus</span>
                        <div className="text-right">
                            <span className="text-purple-400 font-mono block">+€{finalPoolBonus.toFixed(2)}</span>
                            <span className="text-[9px] text-slate-600 block">(Pool x {retMult} x {eduMult})</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- SECTION 2: THE DRIVERS (Tasks) --- */}
            <div className="w-full xl:w-[50%] p-6 flex flex-col justify-center gap-4 relative z-10">

                {/* A. ACTIVITY POOL BREAKDOWN */}
                <div className="grid grid-cols-2 gap-4">
                    {/* Sales Pot (Hidden for Support) */}
                    {role !== 'Support_L1' && (
                        <div className="bg-slate-800/40 p-3 rounded-lg border border-slate-700/50 h-full">
                            <div className="flex items-center gap-2 mb-2 text-blue-400">
                                <TrendingUp size={14} /> <span className="text-xs font-bold uppercase">Sales Pot</span>
                            </div>
                            <div className="flex justify-between text-[10px] text-slate-400 mb-1">
                                <span>Funded (€10)</span> <span className="text-white font-mono">{activity.funded}</span>
                            </div>
                            <div className="flex justify-between text-[10px] text-slate-400">
                                <span>Trades (€5)</span> <span className="text-white font-mono">{activity.activeTraders}</span>
                            </div>
                            <div className="mt-2 pt-2 border-t border-white/5 text-right text-xs font-bold text-blue-300">
                                €{salesPot.toFixed(2)}
                            </div>
                        </div>
                    )}

                    {/* Service Pot (Hidden for Sales) */}
                    {role !== 'Sales_L1' && (
                        <div className="bg-slate-800/40 p-3 rounded-lg border border-slate-700/50 h-full">
                            <div className="flex items-center gap-2 mb-2 text-purple-400">
                                <ThumbsUp size={14} /> <span className="text-xs font-bold uppercase">Service Pot</span>
                            </div>
                            <div className="flex justify-between text-[10px] text-slate-400 mb-1">
                                <span>Tickets (€0.50)</span> <span className="text-white font-mono">{activity.tickets}</span>
                            </div>
                            <div className="flex justify-between text-[10px] text-slate-400 mb-1">
                                <span>Reviews (€2.00)</span> <span className="text-white font-mono">{activity.reviews}</span>
                            </div>
                            <div className="mt-2 pt-2 border-t border-white/5 text-right text-xs font-bold text-purple-300">
                                €{servicePot.toFixed(2)}
                            </div>
                        </div>
                    )}
                </div>

                {/* B. MULTIPLIERS (Restored Progress Bars!) */}
                <div className="space-y-3">
                    {/* Retention Bar */}
                    <div>
                        <div className="flex justify-between text-[10px] mb-1">
                            <span className="text-slate-300 flex items-center gap-1.5">
                                <Award size={12} className="text-amber-400" /> Retention
                            </span>
                            <span className="text-slate-400">Current: <strong className="text-white">{activity.retention}%</strong> <span className="text-amber-400 font-bold ml-1">(x{retMult})</span></span>
                        </div>
                        <div className="w-full h-1.5 bg-slate-700 rounded-full overflow-hidden mb-1">
                            <div className="h-full bg-amber-500" style={{ width: `${Math.min((activity.retention / 90) * 100, 100)}%` }}></div>
                        </div>
                        <p className="text-[9px] text-slate-500 italic">Target: 90% for 1.3x boost</p>
                    </div>

                    {/* Education Bar */}
                    <div>
                        <div className="flex justify-between text-[10px] mb-1">
                            <span className="text-slate-300 flex items-center gap-1.5">
                                <GraduationCap size={12} className="text-blue-400" /> Education
                            </span>
                            <span className="text-slate-400">Current: <strong className="text-white">{activity.education}%</strong> <span className="text-blue-400 font-bold ml-1">(x{eduMult})</span></span>
                        </div>
                        <div className="w-full h-1.5 bg-slate-700 rounded-full overflow-hidden mb-1">
                            <div className="h-full bg-blue-500" style={{ width: `${Math.min((activity.education / 70) * 100, 100)}%` }}></div>
                        </div>
                        <p className="text-[9px] text-slate-500 italic">Target: 70% for 1.3x boost</p>
                    </div>
                </div>
            </div>

            {/* --- SECTION 3: THE GATE (25%) --- */}
            <div className={`w-full xl:w-[25%] flex flex-col items-center justify-center text-center p-6 border-l border-white/10 transition-colors ${isCompliant ? 'bg-green-500/5' : 'bg-red-500/5'}`}>
                <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-3 shadow-lg ${isCompliant ? 'bg-green-500 text-slate-900 shadow-green-500/20' : 'bg-red-500 text-white shadow-red-500/20'}`}>
                    {isCompliant ? <CheckCircle2 size={32} /> : <Lock size={28} />}
                </div>

                <h4 className={`font-bold text-sm mb-2 ${isCompliant ? 'text-green-400' : 'text-red-400'}`}>
                    {isCompliant ? 'BONUS UNLOCKED' : 'BONUS LOCKED'}
                </h4>

                {/* CSAT GATE */}
                <div className="w-full bg-slate-800 rounded-lg p-3 border border-slate-700 text-left mt-2">
                    <div className="flex justify-between items-center mb-1">
                        <span className="text-[10px] font-bold text-slate-400">CSAT GATE</span>
                        <span className={`text-xs font-bold ${activity.csat >= 80 ? 'text-green-400' : 'text-amber-400'}`}>{activity.csat}%</span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-700 rounded-full overflow-hidden mb-2">
                        <div className={`h-full ${activity.csat >= 80 ? 'bg-green-500' : 'bg-amber-500'}`} style={{ width: `${activity.csat}%` }}></div>
                    </div>
                    <p className="text-[9px] text-slate-500 leading-tight">
                        Payout: <strong className="text-white">{(csatPayoutPct * 100).toFixed(0)}%</strong>. Must be &gt;80% for full bonus.
                    </p>
                </div>
            </div>

        </div>
    );
};

// =========================================================
// 11. AI REVIEW CALENDAR (Support Lead)
// =========================================================
export const ReviewCalendarWidget = () => { // <--- WIDGET 11 STARTS HERE
    const reviews = [
        { id: 1, agent: 'Mike Tyson', type: 'Probation Review', due: 'Today', status: 'urgent', aiReady: true },
        { id: 2, agent: 'Jessica Rabbit', type: 'Quarterly Perf.', due: 'In 2 Days', status: 'pending', aiReady: true },
        { id: 3, agent: 'David Beckham', type: 'Monthly 1:1', due: 'Next Week', status: 'upcoming', aiReady: false },
    ];

    return (
        <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 h-full flex flex-col">
            <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-white flex items-center gap-2">
                    <Calendar size={18} className="text-purple-400" /> Review Schedule
                </h3>
                <span className="text-[10px] bg-purple-500/20 text-purple-300 px-2 py-1 rounded border border-purple-500/30 flex items-center gap-1">
                    <Sparkles size={10} /> AI Analysis Active
                </span>
            </div>

            <div className="space-y-3 flex-1 overflow-y-auto pr-2 custom-scrollbar">
                {reviews.map(review => (
                    <div key={review.id} className="p-3 bg-slate-900/50 border border-slate-700 rounded-xl hover:border-purple-500/50 transition-all group cursor-pointer">
                        <div className="flex justify-between items-start mb-2">
                            <div>
                                <h4 className="font-bold text-sm text-white">{review.agent}</h4>
                                <p className="text-xs text-slate-400">{review.type}</p>
                            </div>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${review.status === 'urgent' ? 'bg-red-500/10 text-red-400 border-red-500/20' :
                                    review.status === 'pending' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                                        'bg-blue-500/10 text-blue-400 border-blue-500/20'
                                }`}>
                                {review.due}
                            </span>
                        </div>

                        {/* AI Insight Section */}
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
                            {review.aiReady ? (
                                <div className="flex items-center gap-1.5 text-[10px] text-green-400">
                                    <Sparkles size={10} />
                                    <span>AI Draft Ready</span>
                                </div>
                            ) : (
                                <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                                    <div className="w-2 h-2 rounded-full border-2 border-slate-600 border-t-transparent animate-spin"></div>
                                    <span>AI Analyzing...</span>
                                </div>
                            )}

                            <button className="text-[10px] font-bold text-slate-400 group-hover:text-white flex items-center gap-1 transition-colors">
                                Start Review <ChevronRight size={12} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-auto pt-3 text-center border-t border-slate-700/50">
                <p className="text-[10px] text-slate-500">Next Cycle: <span className="text-slate-300">Q3 Reviews (Oct 15)</span></p>
            </div>
        </div>
    );
};

// =========================================================
// 12. COMPLETED ACTIVITY FEED (LIVE DUAL-STREAM)
// =========================================================
export const CompletedWorkWidget = () => {
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchTodayActivity = async () => {
            try {
                // Set the baseline to midnight today
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                let combinedActivity = [];

                // STREAM 1: Fetch Closed Tickets from Supabase (Completed Today)
                const { data: closedTickets, error: ticketError } = await supabase
                    .from('crm_tickets')
                    .select('*')
                    .eq('status', 'closed')
                    .gte('updated_at', today.toISOString());

                if (!ticketError && closedTickets) {
                    const ticketActs = closedTickets.map(t => ({
                        id: `ticket-${t.id}`,
                        type: 'Ticket Resolved',
                        title: t.subject || 'Client Support Request',
                        agent: 'Support Team', // Connect to agent profiles later if needed
                        timestamp: new Date(t.updated_at),
                        iconColor: 'bg-green-500/20 text-green-400',
                        icon: <CheckCircle size={14} />
                    }));
                    combinedActivity = [...combinedActivity, ...ticketActs];
                }

                // STREAM 2: Fetch KYC Approvals from Nommia API (Verified Today)
                const listResponse = await fetchClientsList(1, 100);
                let clientArray = [];
                if (Array.isArray(listResponse)) {
                    clientArray = listResponse;
                } else if (typeof listResponse === 'object') {
                    const arrayKey = Object.keys(listResponse).find(k => Array.isArray(listResponse[k]));
                    clientArray = arrayKey ? listResponse[arrayKey] : (listResponse.data || []);
                }

                const kycActs = clientArray.filter(c => {
                    // Check if they are verified AND if the verification happened today
                    if (c.kyc?.status === 'VERIFIED' && c.kyc?.updatedAt) {
                        const kycDate = new Date(c.kyc.updatedAt);
                        return kycDate >= today;
                    }
                    return false;
                }).map(c => ({
                    id: `kyc-${c.accountId || c.email}`,
                    type: 'KYC Approved',
                    title: c.firstName ? `${c.firstName} ${c.lastName || ''}`.trim() : c.email,
                    agent: 'Compliance Team',
                    timestamp: new Date(c.kyc.updatedAt),
                    iconColor: 'bg-blue-500/20 text-blue-400',
                    icon: <CheckCircle size={14} />
                }));

                combinedActivity = [...combinedActivity, ...kycActs];

                // Sort everything chronologically (newest first)
                combinedActivity.sort((a, b) => b.timestamp - a.timestamp);

                setActivities(combinedActivity);
            } catch (err) {
                console.error("Failed to load completed activity:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchTodayActivity();
    }, []);

    return (
        <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 h-full flex flex-col">
            <div className="flex justify-between items-center mb-6">
                <h3 className="font-bold text-white flex items-center gap-2">
                    <Activity size={18} className="text-purple-400" /> Completed Today
                </h3>
                {loading ? (
                    <span className="text-[10px] text-slate-400 animate-pulse">Syncing...</span>
                ) : (
                    <span className="bg-purple-500/20 text-purple-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-purple-500/20">
                        {activities.length} Actions
                    </span>
                )}
            </div>

            <div className="space-y-4 flex-1 overflow-y-auto pr-2 custom-scrollbar relative">
                {/* Vertical Timeline Line */}
                <div className="absolute left-[19px] top-2 bottom-2 w-px bg-slate-700/50 z-0"></div>

                {loading ? (
                    <div className="h-full flex flex-col items-center justify-center text-slate-500 z-10 relative">
                        <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mb-2"></div>
                        <p className="text-xs">Compiling agent activity...</p>
                    </div>
                ) : activities.length > 0 ? activities.map((item) => (
                    <div key={item.id} className="relative z-10 flex items-start gap-4 group">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center border border-white/5 shrink-0 shadow-lg ${item.iconColor}`}>
                            {item.icon}
                        </div>

                        <div className="flex-1 bg-slate-800/80 p-3 rounded-xl border border-slate-700 group-hover:border-purple-500/30 transition-colors">
                            <div className="flex justify-between items-start mb-1">
                                <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">{item.type}</h4>
                                <span className="text-[10px] text-slate-500 font-medium">
                                    {item.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                            <p className="text-sm font-medium text-white mb-1">{item.title}</p>
                            <p className="text-[10px] text-slate-400">Completed by: <span className="text-slate-300">{item.agent}</span></p>
                        </div>
                    </div>
                )) : (
                    <div className="h-full flex flex-col items-center justify-center text-slate-500 opacity-60 z-10 relative bg-slate-800/50 rounded-xl border border-slate-700/50 p-6">
                        <CheckCircle size={32} className="mb-2 text-slate-600" />
                        <p className="text-xs text-center">No completed tasks yet today.<br />Let's get to work!</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// =========================================================
// 13. FACTORY
// =========================================================
export const renderDashboardWidget = (widgetId, userRole, onAction, data = {}) => {
    switch (widgetId) {
        // Manager Widgets (Pass onAction to them)
        case 'live_floor': return <LiveFloorWidget key={widgetId} onAction={onAction} />; // <--- 2. PASS IT HERE
        case 'review_calendar': return <ReviewCalendarWidget key={widgetId} onAction={onAction} />; // <--- AND HERE

        // Standard Widgets
        case 'dep_ticker': return <DepositTickerWidget key={widgetId} />;
        case 'team_board': return <LeaderboardWidget key={widgetId} />;
        case 'staff_rota': return <StaffRotaWidget key={widgetId} />;
        case 'completed_work': return <CompletedWorkWidget key={widgetId} />;

        // Agent Widgets
        case 'my_perf': return <MyPerformanceWidget key={widgetId} />;
        case 'contact_queue': return <ContactQueueWidget key={widgetId} />;
        case 'market_events': return <MarketEventsWidget key={widgetId} />;

        // Dynamic Role-Based Widgets
        case 'ticket_queue': return <TicketQueueWidget key={widgetId} role={userRole} onAction={onAction} tickets={data.tickets || []} unassigned={data.unassigned || []} />;
        case 'kyc_queue': return <KYCTaskWidget key={widgetId} role={userRole} onAction={onAction} />;
        case 'hybrid_unified': return <HybridUnifiedWidget key={widgetId} role={userRole} />;

        // Placeholders
        case 'sla_alerts': return (
            <div key={widgetId} className="bg-red-900/20 border border-red-500/30 p-6 rounded-2xl h-full flex flex-col justify-center items-center text-center animate-pulse">
                <AlertTriangle size={32} className="text-red-500 mb-2" />
                <h3 className="text-red-400 font-bold">SLA Warning</h3>
                <p className="text-xs text-red-300 mt-1">3 Tickets Overdue by +2 hours</p>
            </div>
        );

        case 'staff_reports': return (
            <div key={widgetId} className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 h-full flex flex-col items-center justify-center text-slate-500 hover:text-white transition-colors cursor-pointer">
                <Download size={32} className="mb-2" />
                <span className="text-xs font-bold uppercase">Download Reports</span>
            </div>
        );

        default: return null;
    }
};

// =========================================================
// 14. GLOBAL BROADCAST LISTENER (Urgent Popups Only)
// =========================================================
export const GlobalBroadcastListener = () => {
    const [popups, setPopups] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [userRole, setUserRole] = useState(null);

    useEffect(() => {
        // 1. Initialize User
        const initAgentData = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
                setCurrentUser(user.id);
                const { data: profile } = await supabase.from('profiles').select('role').eq('id', user.id).single();
                setUserRole(profile?.role?.toLowerCase() || 'staff');
            }
        };
        initAgentData();

        // 2. Listen for BRAND NEW live messages
        const channel = supabase
            .channel('live-floor-comms')
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'crm_broadcasts' }, (payload) => {
                const newMsg = payload.new;

                let shouldShow = false;
                if (newMsg.target_type === 'all') shouldShow = true;
                else if (newMsg.target_type === 'role' && userRole && userRole.includes(newMsg.target_value.toLowerCase())) shouldShow = true;
                else if (newMsg.target_type === 'user' && newMsg.target_value === currentUser) shouldShow = true;

                if (shouldShow) {
                    const localId = Date.now();

                    // Add to the 20-second flashing popup queue
                    setPopups(prev => [...prev, { ...newMsg, localId }]);

                    // Auto-clear the popup after 20 seconds
                    setTimeout(() => {
                        setPopups(prev => prev.filter(b => b.localId !== localId));
                    }, 20000);
                }
            })
            .subscribe();

        return () => supabase.removeChannel(channel);
    }, [currentUser, userRole]);

    const removePopup = (id) => {
        setPopups(prev => prev.filter(b => b.localId !== id));
    };

    if (popups.length === 0) return null;

    return createPortal(
        <div className="fixed top-6 right-6 z-[9999] flex flex-col gap-3 w-96 pointer-events-none">
            {popups.map(msg => (
                <div key={msg.localId} className="bg-red-600 border border-red-400 p-5 rounded-xl shadow-[0_10px_40px_rgba(220,38,38,0.5)] pointer-events-auto transform transition-all animate-in slide-in-from-right-8 fade-in duration-300">
                    <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-2">
                            <AlertCircle size={18} className="text-white animate-pulse" />
                            <span className="font-bold text-white uppercase text-xs tracking-wider">Priority Floor Broadcast</span>
                        </div>
                        <button onClick={() => removePopup(msg.localId)} className="text-red-300 hover:text-white transition-colors">✕</button>
                    </div>
                    <p className="text-white text-sm font-medium leading-relaxed">{msg.message}</p>
                    <div className="flex justify-between items-center mt-4 border-t border-red-500/50 pt-2">
                        <span className="text-red-200 text-[10px] font-bold">From: {msg.sender_name || msg.sender_email}</span>
                        <span className="text-red-200 text-[10px]">{new Date(msg.created_at).toLocaleTimeString()}</span>
                    </div>
                </div>
            ))}
        </div>,
        document.body
    );
};